import VueRouter, {Route} from 'vue-router';
import {Store} from 'vuex';

/**
 * Detects new client deployments and moves users to the new version.
 *
 * - The build embeds its version as `VUE_APP_VERSION`, `build_client.sh` writes the same value to `version.json`.
 * - `version.json` is polled every {@link CHECK_INTERVAL} ms and when the tab becomes visible again.
 * - New version found: a permanent toast offers "Neu laden"; besides that, the next navigation to another
 *   page reloads the page instead of navigating inside the app (the user leaves the page anyway).
 * - A chunk that fails to load (deployment removed it) reloads the page once.
 *
 * Without `VUE_APP_VERSION` (local dev server) the version check is disabled, the chunk fallback stays active.
 */

const CURRENT_VERSION = process.env.VUE_APP_VERSION;
const VERSION_URL = `${process.env.BASE_URL}version.json`;
const CHECK_INTERVAL = 5 * 60 * 1000;
/** Minimum gap between two checks triggered by the tab becoming visible */
const MIN_CHECK_GAP = 60 * 1000;
/** A second chunk error within this time does not reload again (prevents reload loops) */
const CHUNK_RELOAD_GUARD = 30 * 1000;
const CHUNK_RELOAD_KEY = 'versionCheck.chunkReloadAt';

let updateAvailable = false;
let lastCheck = 0;
/** Target of the running navigation, used to reload onto it after a chunk error */
let pendingTarget: Route | undefined;

async function fetchDeployedVersion(): Promise<string | undefined> {
    try {
        const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {cache: 'no-store'});
        if (!response.ok) {
            return undefined;
        }
        const data = await response.json();
        return typeof data?.version === 'string' ? data.version : undefined;
    } catch {
        // Offline or server restarting: try again with the next check
        return undefined;
    }
}

/** Reloads the page onto the given route (works for hash and history mode) */
function reloadOnto(router: VueRouter, target?: Route) {
    if (target) {
        window.history.replaceState(window.history.state, '', router.resolve(target.fullPath).href);
    }
    window.location.reload();
}

function showUpdateToast(store: Store<any>) {
    store.commit('showSnackbar', {
        text: 'Eine neue Version ist verfügbar.',
        btnColor: 'info',
        timeout: -1,
        actions: [{
            text: 'Neu laden',
            color: 'info',
            fn: () => window.location.reload(),
        }],
    });
}

async function checkVersion(store: Store<any>) {
    lastCheck = Date.now();
    const deployed = await fetchDeployedVersion();
    if (!deployed || deployed === CURRENT_VERSION) {
        return;
    }
    updateAvailable = true;
    // Shown again with every check in case it was pushed out of the toast stack (identical toasts are replaced)
    showUpdateToast(store);
}

function isChunkLoadError(error: unknown): boolean {
    const err = error as { name?: string; message?: string } | undefined;
    return err?.name === 'ChunkLoadError' || /Loading (CSS )?chunk .+ failed/i.test(err?.message || '');
}

function installChunkErrorFallback(router: VueRouter) {
    router.beforeEach((to, from, next) => {
        pendingTarget = to;
        next();
    });
    router.onError((error) => {
        if (!isChunkLoadError(error)) {
            return;
        }
        let lastReload = 0;
        try {
            lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
            if (Date.now() - lastReload < CHUNK_RELOAD_GUARD) {
                return;
            }
            sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
        } catch {
            // sessionStorage unavailable: reload anyway, the browser stops at the second error at the latest
        }
        reloadOnto(router, pendingTarget);
    });
}

function installReloadOnNavigation(router: VueRouter) {
    router.beforeEach((to, from, next) => {
        // Only real page changes: initial navigation and query changes (filters, tabs) stay inside the app
        if (updateAvailable && from.matched.length && to.path !== from.path) {
            next(false);
            reloadOnto(router, to);
            return;
        }
        next();
    });
}

function installVersionPolling(store: Store<any>) {
    window.setInterval(() => checkVersion(store), CHECK_INTERVAL);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && Date.now() - lastCheck > MIN_CHECK_GAP) {
            checkVersion(store);
        }
    });
}

export function installVersionCheck(router: VueRouter, store: Store<any>): void {
    installChunkErrorFallback(router);
    if (!CURRENT_VERSION) {
        return;
    }
    installReloadOnNavigation(router);
    installVersionPolling(store);
}
